/* App.tsx */
import React, { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import jwt_decode from 'jwt-decode';
import { RouterProvider } from 'react-router-dom';
import {
  initializeIcons,
  Spinner,
  SpinnerSize,
  IStackStyles,
} from '@fluentui/react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/link-context';
import { useClientHeight } from './components/hooks';
import { Container, Section, Row } from './components/layout';
import TopBarWithNavigation from './components/parts/TopBarWithNavigation';
import Error from './components/parts/Error';
import Notification from './components/parts/Notification';
import router from './Router';
import './App.css';
import './styles.css';
import { useAppDispatch } from './redux/hooks';
import { setPermission } from './redux/permission/permissionSlice';
import 'react-toastify/dist/ReactToastify.css';

initializeIcons(/* optional base url */);

const wrapperStyle = {
  minHeight: '100vh',
  background:
    'linear-gradient(162deg, rgba(225,235,220,0.26514355742296913) 8%, rgba(255,255,255,0.4640231092436975) 32%, rgba(225,235,220,0.26514355742296913) 46%, rgba(247,251,244,1) 65%, rgba(255,255,255,1) 97%)',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  // display: 'flex',
  // alignItems: 'center',
  // justifyContent: 'center',
  // position: 'relative' as const,
  // overflow: 'hidden' as const,
};

const gradientBackgroundStyle = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(162deg, rgba(225,235,220,0.26514355742296913) 100%, rgba(255,255,255,1) 100%)',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  zIndex: 0,
};

const blurOverlayStyle = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backdropFilter: 'blur(20px)',
  background: 'rgba(255, 255, 255, 0.3)', // Light white overlay for a frosted glass effect
  zIndex: 1,
  borderRadius: '10px',
};

const contentWrapperStyle = {
  position: 'relative' as const,
  zIndex: 2,
  padding: '20px',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.7)', // Adds a subtle frosted glass effect to the content wrapper itself
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
};

const loadingStackStyle: IStackStyles = {
  root: {
    minHeight: '100vh',
  },
};

const backEndUri = `${process.env.REACT_APP_BACKEND_URI}/graphql` || '';

// Paginatie met Prisma `skip`/`take` (zoals findManyLeads): skip 0 vervangt de lijst, zodat een
// refetch geen dubbels geeft; "meer laden" schrijft op positie skip. Is de cache korter dan skip
// (bv. net na een reset), dan plakken we achteraan zoals voorheen, zodat er nooit gaten ontstaan.
// `take` hoort bij de sleutel: een keuzelijst (take 15) en een overzicht (take 20) met dezelfde
// filter delen anders één cache-ingang.
const skipTakePagination = (keyArgs: string[]) => ({
  keyArgs,
  // eslint-disable-next-line default-param-last
  merge(existing: any[] = [], incoming: any[], { args }: { args: Record<string, any> | null }) {
    const skip = args?.skip ?? 0;
    if (skip === 0) return incoming;
    if (existing.length < skip) return [...existing, ...incoming];
    const merged = existing.slice(0);
    for (let i = 0; i < incoming.length; i += 1) {
      merged[skip + i] = incoming[i];
    }
    return merged;
  },
});

// De backend telt enkel op de eerste pagina; "meer laden" (skip > 0) geeft prisma_total = null.
// Apollo bewaart elk record maar één keer: zonder deze regel zou zo'n null het totaal wissen van
// een lijst waarin datzelfde record de eerste rij is (de teller leest lijst[0].prisma_total).
// Een nieuwe filter vraagt altijd pagina 0 op, en die levert een echt totaal dat wel overschrijft.
const TYPES_WITH_TOTAL = [
  'Architect', 'Asset', 'Car', 'Construction_site', 'Cost_center', 'Cost_center_wings',
  'Customer', 'Customer_contract_update', 'Customer_invoice', 'Customer_payment_transaction',
  'Defect_work_order', 'Employee', 'Incoming_invoice', 'Lead', 'Log_item', 'Mail_template',
  'Plan', 'Purchase_order', 'Quote_reference', 'Sales_rep', 'Supplier', 'Suspect', 'Tax_code',
  'Team', 'Time_sheet_entry', 'Timesheet', 'Water_point',
];
const keepTotalPolicies = TYPES_WITH_TOTAL.reduce((policies, typeName) => ({
  ...policies,
  [typeName]: {
    fields: {
      prisma_total: {
        // nieuw totaal wint; leeg (null) laat het vorige staan; zonder vorig blijft het null
        // (nooit undefined: dan zou Apollo het veld als ontbrekend zien en de lijst onvolledig)
        merge: (existing: number | null | undefined, incoming: number | null | undefined) => (
          incoming ?? existing ?? null
        ),
      },
    },
  },
}), {} as Record<string, any>);

function App() {
  const dispatch = useAppDispatch();
  const [headerRef, headerHeight] = useClientHeight();
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // De client (en dus de cache) wordt maar één keer aangemaakt. Vroeger werd hij bij
  // elke render van App opnieuw gemaakt (bv. bij het verkleinen van het venster),
  // waardoor de cache telkens leeg was en alles opnieuw werd opgevraagd.
  // Het token wordt per vraag opgehaald: getAccessTokenSilently cachet het en
  // vernieuwt het vanzelf, zodat vragen na het verlopen van het token niet stil falen.
  const client = useMemo(() => {
  const httpLink = createHttpLink({
    uri: backEndUri,
  });

  const authLink = setContext(async (_, { headers }) => {
    const accessToken = await getAccessTokenSilently().catch(() => undefined);
    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            findManyConstructionSites: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyPlans: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyLogItems: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyQuoteReferences: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyEmployees: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyLeads: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyAssets: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyCars: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyTeams: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyDefectWorkOrders: skipTakePagination(['filter', 'orderBy', 'take']),
            findManySuppliers: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyCustomerPaymentTransactions: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyCustomerContractUpdates: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyCustomerInvoices: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyCostCenters: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyIncomingInvoices: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyTemplateItems: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyArchitects: skipTakePagination(['filter', 'orderBy', 'take']),
            findManyTaxCodes: skipTakePagination(['filter', 'orderBy', 'take']),
          },
        },
        ...keepTotalPolicies,
      },
    }),
    /* defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
    }, */
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then(token => {
        const decoded: any = jwt_decode(token);
        // altijd zetten (ook leeg), zodat PrivateRoute weet dat de rechten gekend zijn
        dispatch(setPermission(
          decoded && typeof decoded === 'object' && Array.isArray(decoded.permissions)
            ? decoded.permissions
            : [],
        ));
      }).catch(() => dispatch(setPermission([])));
    }
  }, [isAuthenticated]);

  return (
    <ApolloProvider client={client}>
      <div style={wrapperStyle}>
        {/* <div style={gradientBackgroundStyle} />
        <div style={blurOverlayStyle} /> */}
        <div>
          <TopBarWithNavigation
            headerRef={headerRef}
            headerHeight={headerHeight}
            onNavigate={(path: string) => router.navigate(path)}
          />
          {!isLoading && (
            <Container wrapperStyle={{ top: headerHeight || 41 }}>
              <Section>
                <Row
                  id='content'
                  style={{
                    backgroundColor: 'white',
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                    borderRadius: '10px',
                  }}
                >
                  <RouterProvider router={router} />
                </Row>
              </Section>
            </Container>
          )}
          {isLoading && (
            <Container
              verticalAlign='center'
              styles={loadingStackStyle}
              wrapperStyle={{ minHeight: '100vh' }}
            >
              <Spinner size={SpinnerSize.large} />
            </Container>
          )}
          <Error />
          <Notification />
        </div>
      </div>
    </ApolloProvider>
  );
}

export default App;
